<template>
  <div
    class="mastery-sidebar-component"
    :class="{ v2: questionUiV2Enabled }"
  >
    <ul class="nav-items-container">
      <li
        v-for="navItem in navigationItems"
        :key="navItem.id"
        :class="['nav-item', { active: navItemActive(navItem) }]"
      >
        <router-link
          v-disableable="{blur: onTour, dim: onTour, disabled: onTour }"
          :to="navItem.route"
          class="nav-item-link"
          @click.native="navItemClicked(navItem)"
        >
          <img
            :src="navItemIcon(navItem)"
            class="nav-item-icon"
            alt=""
            role="presentation"
          >
          <span class="nav-item-name">
            {{ navItem.name }}
          </span>
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script>
import DashboardMixin from '@/mixins/dashboard';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';

export default {
  name: 'MasterySidebar',
  mixins: [DashboardMixin, QuestionsUIV2Mixin],
  computed: {
    active() {
      // if route has no ties to web-main group, don't do anything
      // secondaryRoute on meta just means it doesn't live in main content
      if (this.$route.meta.secondaryRoute) return {};
      // see if one of the items matches query directly first
      const queryItem = this.navigationItems.find(ni => ni.matchesRouteQuery(this.$route));
      if (queryItem) return queryItem;

      // otherwise see if last visited dashboard has a matching menu item
      const lastDashboardIds = this.$store.getters['dashboards/lastVisitedId'];
      const dashboardItemMatch = this.navigationItems.find(ni => {
        // Dashboard group ids can be in the lastVisitedId array so we want to check
        // if the first item in the lastVisitedId array is the id of a dashboard group
        // that is found in the main nav. This will make sure that we are only matching
        // the ids of the dashboard group with the root level id in lastDashboardIds to
        // prevent checking dashboard ids against dashboard group ids.
        if (ni.destination === 'dashboard_group') {
          return lastDashboardIds[0] === ni.resourceId;
        }

        return ni.destination === 'dashboard' && lastDashboardIds.includes(ni.resourceId);
      });

      if (dashboardItemMatch) return dashboardItemMatch;

      // otherwise see if there's a home menu item that matches the last dashboard item
      const homeDashboard = this.$store.getters['dashboards/menuDefaultDashboard'];
      if (homeDashboard) {
        const homeItemMatch = this.navigationItems.find(ni => {
          return ni.destination === 'dashboard' && ni.resourceId === homeDashboard.id;
        });

        if (homeItemMatch) return homeItemMatch;
      }

      // lastly see if there's a home item and our path is /
      if (this.$route.path === '/') {
        const homeItemPathMatch = this.navigationItems.find(ni => {
          return ni.destination === 'home';
        });

        if (homeItemPathMatch) return homeItemPathMatch;
      }

      return {};
    },
    navigationItems() {
      // hides the onboarding item for questions tour
      const ni = this.$store.getters['navigationItems/menuItems'];
      return ni.filter(ni => !ni.dynamic && this.canAccessDashboardResource(ni));
    },
    onTour() {
      return this.$store.getters['session/onTour'];
    },
  },
  methods: {
    navItemActive(navItem) {
      return this.active.id === navItem.id;
    },
    navItemClicked(navItem) {
      const navGroup = this.$store.getters['navigationGroups/navigationGroups'][navItem.navigationGroupId];
      this.$analytics.events.selectedNavItem(navItem, navGroup, null);
    },
    navItemIcon(navItem) {
      return this.navItemActive(navItem) ? navItem.activeIcon : navItem.icon;
    },
  },
};
</script>

<style lang="scss" scoped>
.mastery-sidebar-component {
  position: fixed;
  background-color: #f7f7f7;
  width: rem-calc(88);
  top: rem-calc(48);
  transition: .1s linear width;
  bottom: 0;
  z-index: 900;
  display: flex;
  justify-content: center;
  align-items: center;

  &.v2 {
    background-color: white;
    border-right: 1px solid #E3E8EF;
  }

  .nav-items-container {
    padding: 0;
    margin: 0;
    list-style-type: none;
    width: 100%;
    display: flex;
    flex-direction: column;

    .nav-item {
      flex: 1 0 0;
      margin: rem-calc(12) 0;

      .nav-item-link {
        display: block;
        width: 100%;
        height: rem-calc(40);
        padding: rem-calc(8) 0;

        .nav-item-icon {
          display: block;
          height: rem-calc(24);
          width: rem-calc(24);
          margin: 0 auto;
          margin-bottom: rem-calc(8);
          object-fit: cover;
          transition: .25s linear all;;
        }

        .nav-item-name {
          display: none;
          text-align: center;
          color: #000000;
          font-size: rem-calc(12);
        }
      }

      &.active {
        .nav-item-name {
          font-weight: $semi-bold;
        }
      }
    }
  }

  &:hover {
    width: rem-calc(132);

    .nav-items-container {
      .nav-item {
        .nav-item-link {
          .nav-item-name {
            display: block;
          }
        }
      }
    }
  }

  @include breakpoint(small) {
    display: flex;
    bottom: 0;
    left: 0;
    right: 0;
    height: rem-calc(72);
    border-top: 1px solid #e6ecf0;
    top: initial;
    width: initial;
    background-color: #ffffff;
    align-items: flex-start;

    .nav-items-container {
      flex-direction: row;
      padding: rem-calc(4) 0 0;
      .nav-item {
        margin: 0;
        .nav-item-link {
          .nav-item-name {
            font-size: rem-calc(10);
            display: block;
            color: #222222;
          }
        }
      }
    }

    &:hover {
      width: initial;
    }
  }
}
</style>
